<template>
    <div>
        <rkm-form ref="Form">
            <rkm-row class="flex-nowrap">
                <rkm-col class="flex-grow-0">
                    <div
                        class="senha d-flex justify-content-center align-items-center"
                        :style="senhaStyle"
                    >
                        <h2>
                            <strong>{{ form.senha }}</strong>
                        </h2>
                    </div>
                </rkm-col>

                <rkm-col class="flex-grow-1">
                    <rkm-row
                        v-if="hasPaciente"
                        :class="[`h-${hasPaciente ? '100' : '75'}`]"
                    >
                        <rkm-col class="d-flex flex-column justify-content-around">
                            <rkm-row>
                                <rkm-col>
                                    <rkm-form-input
                                        v-model="form.motivoUrgencia"
                                        :validations="validations.motivoUrgencia"
                                        name="Motivo urgência"
                                        :disabled="!form.isUrgente"
                                        uppercase
                                        class="m-0"
                                    >
                                        <rkm-input-group-prepend slot="prepend">
                                            <rkm-input-group-text>
                                                <rkm-form-checkbox v-model="form.isUrgente">
                                                    Urgente
                                                </rkm-form-checkbox>
                                            </rkm-input-group-text>
                                        </rkm-input-group-prepend>
                                    </rkm-form-input>
                                </rkm-col>
                            </rkm-row>

                            <rkm-row>
                                <rkm-col>
                                    <rkm-form-select
                                        v-model="form.prioridadeId"
                                        name="Prioridade"
                                        valueField="id"
                                        textField="nome"
                                        :validations="validations.prioridade"
                                        :options="prioridades"
                                        show-default-option
                                        :disabled="!form.isAtendimentoPreferencial"
                                        class="m-0"
                                    >
                                        <rkm-input-group-prepend slot="prepend">
                                            <rkm-input-group-text>
                                                <rkm-form-checkbox v-model="form.isAtendimentoPreferencial">
                                                    Atendimento preferencial
                                                </rkm-form-checkbox>
                                            </rkm-input-group-text>
                                        </rkm-input-group-prepend>
                                    </rkm-form-select>
                                </rkm-col>
                            </rkm-row>
                        </rkm-col>
                    </rkm-row>
                </rkm-col>
            </rkm-row>

            <rkm-area
                title="Paciente"
                type="fieldset"
            >
                <template v-if="hasPaciente">
                    <rkm-row>
                        <rkm-col cols="3">
                            <rkm-form-input
                                v-model="form.pacienteIdentificado.prontuario"
                                label="Prontuário"
                                disabled
                            />
                        </rkm-col>

                        <rkm-col cols="3">
                            <rkm-form-input
                                v-model="form.pacienteIdentificado.cns"
                                label="CNS"
                                disabled
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-input
                                v-model="form.pacienteIdentificado.nome"
                                label="Nome"
                                disabled
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-input
                                v-model="form.pacienteIdentificado.nomeSocial"
                                label="Nome social"
                                disabled
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-input
                                v-model="form.pacienteIdentificado.nomeMae"
                                label="Nome da mãe"
                                disabled
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="3">
                            <rkm-form-input
                                v-model="form.pacienteIdentificado.sexo.descricao"
                                label="Sexo"
                                disabled
                            />
                        </rkm-col>

                        <rkm-col cols="3">
                            <rkm-form-input
                                v-model="form.pacienteIdentificado.dataNascimento"
                                label="Data de nascimento"
                                disabled
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-group
                                label="Idade"
                                :label-for="uniqueId('age')"
                            >
                                <div
                                    :id="uniqueId('age')"
                                    class="age d-flex align-items-center justify-content-around"
                                >
                                    <div class="d-flex flex-column align-items-center">
                                        <span>
                                            <strong>{{ form.pacienteIdentificado.idade.anos }}</strong>
                                        </span>

                                        <span>
                                            {{ form.pacienteIdentificado.idade.anos > 1 ? 'Anos' : 'Ano' }}
                                        </span>
                                    </div>

                                    <div class="d-flex flex-column align-items-center">
                                        <span>
                                            <strong>{{ form.pacienteIdentificado.idade.meses }}</strong>
                                        </span>

                                        <span>
                                            {{ form.pacienteIdentificado.idade.meses > 1 ? 'Meses' : 'Mês' }}
                                        </span>
                                    </div>

                                    <div class="d-flex flex-column align-items-center">
                                        <span>
                                            <strong>{{ form.pacienteIdentificado.idade.dias }}</strong>
                                        </span>

                                        <span>
                                            {{ form.pacienteIdentificado.idade.dias > 1 ? 'Dias' : 'Dia' }}
                                        </span>
                                    </div>
                                </div>
                            </rkm-form-group>
                        </rkm-col>
                    </rkm-row>

                    <rkm-area title="Endereço">
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-input
                                    v-model="form.pacienteIdentificado.endereco"
                                    disabled
                                />
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area title="Telefones">
                        <rkm-row>
                            <rkm-col>
                                <Telefones
                                    ref="telefones"
                                    readonly
                                    :telefones="form.pacienteIdentificado.telefones"
                                />
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-row>
                        <rkm-col class="text-right">
                            <rkm-button
                                v-if="handleEdit(form.pacienteIdentificado)"
                                :label="false"
                                color="primary"
                                @click.stop="$refs.ModalPacienteCadastroSimplificado.open(pacienteId)"
                            >
                                Atualizar cadastro
                            </rkm-button>
                        </rkm-col>
                    </rkm-row>

                    <ModalPacienteCadastroSimplificado
                        ref="ModalPacienteCadastroSimplificado"
                        @save="reloadPaciente($event)"
                    />

                    <!-- TODO: Utilizar o componente que será criado para exibir o histórico -->
                </template>

                <template v-else>
                    <rkm-row>
                        <rkm-col cols="3">
                            <rkm-form-select
                                label="Sexo"
                                text-field="descricao"
                                value-field="id"
                                v-model="form.pacienteDesconhecido.sexoId"
                                :options="form.pacienteDesconhecido.sexos"
                                :validations="validations.pacienteDesconhecido.sexo"
                                show-default-option
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-textarea
                                label="Características físicas"
                                rows="5"
                                v-model="form.pacienteDesconhecido.caracteristicasFisicas"
                                :validations="validations.pacienteDesconhecido.caracteristicasFisicas"
                            />
                        </rkm-col>
                    </rkm-row>
                </template>
            </rkm-area>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-input
                        v-model="atendidoEm"
                        label="Data do atendimento"
                        disabled
                    />
                </rkm-col>

                <rkm-col cols="9">
                    <rkm-form-select
                        v-model="form.opcaoEntradaId"
                        label="Opção de entrada"
                        valueField="id"
                        textField="nome"
                        :validations="validations.opcaoEntrada"
                        :options="form.opcoesEntrada"
                        :disabled="!!filaId"
                        show-default-option
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        v-model="form.motivoAtendimento"
                        label="Motivo do atendimento"
                        :validations="validations.motivoAtendimento"
                        :rows="2"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-input
                        v-model="form.acompanhante"
                        label="Acompanhante"
                        :validations="validations.acompanhante"
                        :disabled="form.semAcompanhante"
                        uppercase
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-input-group-text>
                                <rkm-form-checkbox v-model="form.semAcompanhante">
                                    Sem acompanhante
                                </rkm-form-checkbox>
                            </rkm-input-group-text>
                        </rkm-input-group-append>
                    </rkm-form-input>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="7">
                    <rkm-form-input-autocomplete
                        label="Unidade solicitante"
                        :searchFields="form.autocompleteUnidadeEncaminhamentoFields"
                        :repository="repositoryAutoCompleteUnidadeEncaminhamento"
                        textField="nomeFantasia"
                        v-model="form.unidadeEncaminhamento"
                        :validations="validations.unidadeEncaminhamento"
                        :disabled="!form.isEncaminhamento"
                        uppercase
                    >
                        <rkm-input-group-prepend slot="prepend">
                            <rkm-input-group-text>
                                <rkm-form-checkbox v-model="form.isEncaminhamento">
                                    Encaminhamento
                                </rkm-form-checkbox>
                            </rkm-input-group-text>

                            <rkm-form-select
                                :options="form.origensUnidade"
                                textField="nome"
                                v-model="form.origemUnidadeValue"
                                valueField="id"
                                :disabled="!form.isEncaminhamento"
                                class="m-0"
                            />
                        </rkm-input-group-prepend>

                        <rkm-input-group-append
                            v-if="isOrigemExterna"
                            slot="append"
                        >
                            <rkm-button
                                color="primary"
                                title="Cadastrar unidade"
                                @click="$refs.UnidadeExterna.open()"
                                :disabled="!form.isEncaminhamento"
                            >
                                <rkm-icon icon="plus" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col cols="5">
                    <rkm-form-input
                        :validations="validations.medicoSolicitanteEncaminhamento"
                        label="Médico solicitante"
                        uppercase
                        v-model="form.medicoSolicitanteEncaminhamento"
                        :disabled="!form.isEncaminhamento"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="form.isEncaminhamento"
                        :options="form.origensEntrada"
                        :validations="validations.origemEntrada"
                        label="Origem de entrada"
                        textField="nome"
                        v-model="form.origemEntradaId"
                        valueField="id"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="9">
                    <rkm-form-input
                        v-model="form.medicoSamu"
                        label="Médico(a) SAMU"
                        :validations="validations.medicoSamu"
                        :disabled="!isOrigemSamu"
                        uppercase
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        v-model="form.servicoId"
                        label="Serviço"
                        valueField="id"
                        textField="nome"
                        :validations="validations.servico"
                        :options="form.servicos"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="9">
                    <rkm-form-select
                        v-model="form.ocupacaoId"
                        :disabled="!isServicoConsultaMedica"
                        label="Especialidade médica"
                        valueField="id"
                        textField="codigoNome"
                        :validations="validations.ocupacao"
                        :options="form.ocupacoes"
                        show-default-option
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-select
                        v-model="form.centroCustoIdPara"
                        label="Setor de encaminhamento"
                        valueField="id"
                        textField="nome"
                        :validations="validations.centroCustoPara"
                        :options="centrosCustoPara"
                        show-default-option
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        label="Observação"
                        v-model="form.observacao"
                        :validations="validations.observacao"
                        name="observacao"
                        :rows="5"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-fab-area>
            <rkm-fab>
                <rkm-fab-item
                    icon="align-justify"
                    color="primary"
                    label="Plantonistas"
                    @click="$refs.ModalPlantonista.open()"
                />
            </rkm-fab>
        </rkm-fab-area>

        <modal-plantonista ref="ModalPlantonista" />

        <unidade-externa
            ref="UnidadeExterna"
            @save="form.unidadeEncaminhamento = $event"
        />
    </div>
</template>

<script>
// Vuex.
import { mapActions } from 'vuex'

// Mixins.
import { error, data, formats, mask, unmask, repository, object, stringCase } from '@/mixins'

// Enums.
import prioridade from '@/enums/painel-senha/prioridade'
import origemEntrada from '@/enums/pronto-atendimento/origemEntrada'
import servico from '@/enums/cadastro/estabelecimento/servico'
import sexo from '@/enums/cadastro/cidadao/sexo'
import statusPlantao from '@/enums/pronto-atendimento/plantao/status'
import origemUnidadeEncaminhamento from '@/enums/pronto-atendimento/origemUnidadeEncaminhamento'
import setorTipo from '@/enums/cadastro/estabelecimento/setorTipo'
import classificacao from '@/enums/cadastro/paciente/classificacao'

// Components.
import Telefones from '@/components/cadastro/cidadao/Telefone'
import ModalPacienteCadastroSimplificado from '@/components/cadastro/paciente/components/cadastro-simplificado/Modal'
import UnidadeExterna from '@/components/cadastro/unidade-externa/Modal'
import ModalPlantonista from '@/components/pronto-atendimento/plantao/components/plantonista/Modal'

export default {
    mixins: [
        error,
        data,
        formats,
        mask,
        unmask,
        repository,
        object,
        stringCase,
    ],
    components: {
        Telefones,
        ModalPacienteCadastroSimplificado,
        UnidadeExterna,
        ModalPlantonista,
    },
    props: {
        pacienteId: { type: Number, default: null },
        filaId: { type: Number, default: null },
        plantaoId: { type: Number, default: null },
        dataAtendimento: { type: Date },
    },
    data: () => ({
        dataAtual: '',
        termosInvalidos: [],
        repositories: {
            atendimentoRecepcao: null,
            unidade: null,
            unidadeExterna: null,
        },
        enums: {
            prioridade,
            origemEntrada,
            servico,
            sexo,
            statusPlantao,
            origemUnidadeEncaminhamento,
            setorTipo,
            classificacao,
        },
        form: {
            senha: '',
            isUrgente: false,
            motivoUrgencia: '',
            isAtendimentoPreferencial: false,
            prioridades: [],
            prioridadeId: null,
            pacienteIdentificado: {
                prontuario: '',
                cns: '',
                nome: '',
                nomeSocial: '',
                nomeMae: '',
                sexo: {
                    id: null,
                    descricao: ''
                },
                dataNascimento: '',
                idade: {
                    anos: 0,
                    meses: 0,
                    dias: 0,
                },
                endereco: '',
                telefones: [],
            },
            pacienteDesconhecido: {
                sexos: [],
                sexoId: null,
                caracteristicasFisicas: ''
            },
            opcoesEntrada: [],
            opcaoEntradaId: null,
            origensEntrada: [],
            origemEntradaId: null,
            medicoSamu: '',
            motivoAtendimento: '',
            acompanhante: '',
            semAcompanhante: false,
            isEncaminhamento: false,
            autocompleteUnidadeEncaminhamentoFields: [
                { key: 'nome_fantasia', searchable: true },
            ],
            origensUnidade: [],
            origemUnidadeValue: null,
            unidadeEncaminhamento: null,
            medicoSolicitanteEncaminhamento: '',
            servicos: [],
            servicoId: null,
            ocupacoes: [],
            ocupacaoId: null,
            centrosCustoPara: [],
            centroCustoIdPara: null,
            observacao: '',
        },
    }),
    computed: {
        senhaStyle() {

            return {
                minWidth: `${this.hasPaciente ? 180 : 200}px`,
                minHeight: `${this.hasPaciente ? 180 : 220}px`,
            }
        },
        hasPaciente() {

            return !!this.pacienteId
        },
        isOrigemExterna() {

            return this.form.origemUnidadeValue === this.enums.origemUnidadeEncaminhamento.EXTERNA
        },
        repositoryAutoCompleteUnidadeEncaminhamento() {

            return this.form.origemUnidadeValue == this.enums.origemUnidadeEncaminhamento.INTERNA
                ? this.repositories.unidade
                : this.repositories.unidadeExterna
        },
        validations() {

            return {
                pacienteDesconhecido: {
                    sexo: { required: true },
                    caracteristicasFisicas: { required: true, max: 500 }
                },
                motivoUrgencia: { required: this.form.isUrgente, max: 100 },
                prioridade: { required: this.form.isAtendimentoPreferencial },
                opcaoEntrada: { required: !this.filaId },
                origemEntrada: {},
                medicoSamu: { max: 150 },
                motivoAtendimento: { required: true, max: 3000 },
                acompanhante: {
                    required: this.isPacienteMinor && !this.form.semAcompanhante,
                    min: 6,
                    max: 150,
                    termo_invalido: this.termosInvalidos,
                    name: true,
                },
                unidadeEncaminhamento: { required: this.form.isEncaminhamento },
                medicoSolicitanteEncaminhamento: {
                    required: this.form.isEncaminhamento,
                    min: 6,
                    max: 150,
                    name: true,
                },
                servico: { required: true },
                ocupacao: { required: this.isServicoConsultaMedica },
                centroCustoPara: { required: true },
                observacao: { max: 3000 }
            }
        },
        atendidoEm() {

            return this.getFormatedDate(this.dataAtendimento, 'DD/MM/YYYY HH:mm:ss')
        },
        prioridades() {

            if (this.isPacienteMasculino) {

                return this.form.prioridades.filter(p => p.id !== this.enums.prioridade.GESTANTE)
            }

            return this.form.prioridades
        },
        isPacienteMasculino() {

            if (this.pacienteId) {

                return this.form.pacienteIdentificado.sexo.id === this.enums.sexo.MASCULINO
            }

            return this.form.pacienteDesconhecido.sexoId === this.enums.sexo.MASCULINO
        },
        isPacienteMinor() {

            if (this.pacienteId) {

                return this.form.pacienteIdentificado.idade.anos < 18
            }

            return false
        },
        isPacienteDesconhecido() {

            return !this.hasPaciente
        },
        isOrigemSamu() {

            return this.form.origemEntradaId === this.enums.origemEntrada.SAMU
        },
        isServicoConsultaMedica() {

            return this.form.servicoId === this.enums.servico.CONSULTA_MEDICA
        },
        centrosCustoPara() {

            if (!this.form.isEncaminhamento) {

                return this.form.centrosCustoPara.filter(cc => ![
                    this.enums.setorTipo.EXAMES,
                    this.enums.setorTipo.ENFERMAGEM,
                ].includes(cc.setorTipoId))
            }

            return this.form.centrosCustoPara
        }
    },
    watch: {
        'form.isUrgente': function (isUrgente) {

            if (!isUrgente) {

                this.form.motivoUrgencia = ''
            }
        },
        'form.isAtendimentoPreferencial': function (isAtendimentoPreferencial) {

            if (!isAtendimentoPreferencial) {

                this.form.prioridadeId = null
            }

        },
        'form.semAcompanhante': function (semAcompanhante) {

            if (semAcompanhante) {

                this.form.acompanhante = ''
            }
        },
        'form.isEncaminhamento': function (isEncaminhamento) {

            if (isEncaminhamento && this.form.origemEntradaId !== null) {

                const confirm = async () => {

                    this.form.origemEntradaId = null
                }

                const cancel = async () => this.form.isEncaminhamento = false

                const msg = 'Para pacientes encaminhados, precisamos limpar e desabilitar as '
                    + 'informações de Origem de Entrada. Deseja continuar?'

                this.showConfirmation(msg, confirm, cancel)
            }
            else {

                this.form.unidadeEncaminhamento = null
                this.form.medicoSolicitanteEncaminhamento = ''
            }

            if  (this.form.centroCustoIdPara && !isEncaminhamento) {

                const isExames = this.form.centrosCustoPara
                    .find(cc => cc.id === this.form.centroCustoIdPara).setorTipoId === this.enums.setorTipo.EXAMES

                if (isExames) {

                    this.form.centroCustoIdPara = null
                }
            }
        },
        'form.origemUnidadeValue': function () {

            this.form.unidadeEncaminhamento = null
        },
        'form.origemEntradaId': function (origemEntradaId) {

            if (origemEntradaId !== this.enums.origemEntrada.SAMU) {

                this.form.medicoSamu = ''
            }
        },
        'form.servicoId': function (servicoId) {

            if (servicoId !== this.enums.servico.CONSULTA_MEDICA) {

                this.form.ocupacaoId = null
            }
        },
        'form.opcaoEntradaId': function (opcaoEntradaId) {

            if (this.form.isAtendimentoPreferencial != true){

                this.form.isAtendimentoPreferencial = false
            }

            if (opcaoEntradaId) {
                const opcaoEntrada = this.form.opcoesEntrada.find(oe => oe.id === opcaoEntradaId)

                if (opcaoEntrada.prioridadeObrigatoria) {

                    this.form.isAtendimentoPreferencial = true
                }
            }
        }
    },
    created() {

        this.repositories.atendimentoRecepcao = this.getConectaSusRepository('prontoAtendimento.atendimentoRecepcao')
        this.repositories.unidade = this.getConectaSusRepository('cadastro.estabelecimento.unidade')
        this.repositories.unidadeExterna = this.getConectaSusRepository('cadastro.estabelecimento.unidadeExterna')

        this.load()
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            const params = {}

            if (this.pacienteId) {

                params.paciente_id = this.pacienteId
            }

            if (this.filaId) {

                params.fila_id = this.filaId
            }

            this.repositories.atendimentoRecepcao.create(params)
                .then(({ data }) => {

                    this.handleAlertFaaAberta(data)
                    this.handleAlertFaaEvadida(data)

                    this.termosInvalidos = data.termosInvalidos

                    this.form.senha = data.form.senha
                    this.form.prioridades = data.form.prioridades
                    this.form.isPermissaoCadastro = data.form.isPermissaoCadastro
                    this.form.classificacoes = data.form.classificacoes

                    if (data.form.pacienteIdentificado) {

                        this.form.pacienteIdentificado.prontuario = data.form.pacienteIdentificado.prontuario
                        this.form.pacienteIdentificado.cns = data.form.pacienteIdentificado.cns
                        this.form.pacienteIdentificado.nome = data.form.pacienteIdentificado.nome
                        this.form.pacienteIdentificado.nomeSocial = data.form.pacienteIdentificado.nomeSocial
                        this.form.pacienteIdentificado.nomeMae = data.form.pacienteIdentificado.nomeMae
                        this.form.pacienteIdentificado.sexo.id = data.form.pacienteIdentificado.sexo.id
                        this.form.pacienteIdentificado.sexo.descricao = data.form.pacienteIdentificado.sexo.descricao
                        this.form.pacienteIdentificado.dataNascimento = data.form.pacienteIdentificado.dataNascimento
                        this.form.pacienteIdentificado.idade.anos = data.form.pacienteIdentificado.idade.anos
                        this.form.pacienteIdentificado.idade.meses = data.form.pacienteIdentificado.idade.meses
                        this.form.pacienteIdentificado.idade.dias = data.form.pacienteIdentificado.idade.dias
                        this.form.pacienteIdentificado.endereco = data.form.pacienteIdentificado.endereco
                        this.form.pacienteIdentificado.telefones = data.form.pacienteIdentificado.telefones
                        this.form.pacienteIdentificado.classificacaoId = data.form.pacienteIdentificado.classificacaoId
                    }

                    this.form.pacienteDesconhecido.sexos = data.form.pacienteDesconhecido.sexos
                    this.form.opcoesEntrada = data.form.opcoesEntrada
                    this.form.origensEntrada = data.form.origensEntrada
                    this.form.origensUnidade = data.form.origensUnidade
                    this.form.servicos = data.form.servicos
                    this.form.ocupacoes = data.form.ocupacoes
                    this.form.centrosCustoPara = data.form.centrosCustoPara

                    this.form.isAtendimentoPreferencial = data.form.isAtendimentoPreferencial

                    this.$nextTick()
                        .then(() => {

                            this.form.prioridadeId = data.form.prioridadeId

                            return this.$nextTick()
                        })
                        .then(() => {

                            this.form.opcaoEntradaId = data.form.opcaoEntradaId
                            this.form.origemEntradaId = data.form.origemEntradaId
                            this.form.origemUnidadeValue = data.form.origemUnidadeValue

                            this.$emit('paciente', data.form.pacienteIdentificado)
                        })

                })
                .catch((error) => {

                    this.handleError(error)
                    this.$router.push({name: 'pronto-atendimento-atendimento-recepcao-paciente'})
                })
                .finally(() => this.unsetLoading())
        },
        handleAlertFaaAberta(data) {

            if (data.form.alertFaaAberta) {

                this.showConfirmation(
                    `O paciente já possui uma FAA em aberto em <strong>${data.form.setor}</strong>. Deseja continuar?`,
                    async () => {},
                    async () => this.$router.push({ name: 'pronto-atendimento-atendimento-recepcao-paciente' })
                )
            }
        },
        handleAlertFaaEvadida(data) {
            if (data.form.alertFaaEvadida) {

                this.showConfirmation(
                    `O paciente foi evadido as ${data.form.dataHoraDesfecho}. Deseja continuar?`,
                    async () => {},
                    async () => this.$router.push({ name: 'pronto-atendimento-atendimento-recepcao-paciente' })
                )
            }
        },
        save() {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    this.setLoading()

                    this.repositories.atendimentoRecepcao.store(this.prepareData())
                        .then(({ data }) => {

                            this.showSaveNotification()

                            this.$emit('save', data)
                        })
                        .catch((error) => {

                            this.handleError(error)
                        })
                        .finally(() => this.unsetLoading())
                })
        },
        prepareData() {

            return this.camelObjectKeysToSnakeRecursive({
                plantaoId: this.plantaoId,
                fila: this.getFilaData(),
                paciente: this.getPacienteData(),
                atendidoEm: this.getFormatedDate(this.dataAtendimento, 'YYYY-MM-DD HH:mm:ss'),
                urgente: this.isPacienteDesconhecido ? null : this.form.isUrgente,
                urgenteMotivo: this.form.motivoUrgencia,
                origemEntradaId: this.form.origemEntradaId,
                medicoSamu: this.form.medicoSamu,
                acompanhante: this.form.acompanhante,
                unidadeEncaminhamentoId: this.getUnidadeEncaminhamento(
                    this.enums.origemUnidadeEncaminhamento.INTERNA
                ),
                unidadeExternaEncaminhamentoId: this.getUnidadeEncaminhamento(
                    this.enums.origemUnidadeEncaminhamento.EXTERNA
                ),
                medicoSolicitanteEncaminhamento: this.form.medicoSolicitanteEncaminhamento,
                motivoAtendimento: this.form.motivoAtendimento,
                servicoId: this.form.servicoId,
                ocupacaoId: this.form.ocupacaoId,
                observacao: this.form.observacao
            })
        },
        getFilaData() {

            const data = {
                prioridadeId: this.isPacienteDesconhecido ? null : this.form.prioridadeId,
                centroCustoIdPara: this.form.centroCustoIdPara,
            }

            if (this.filaId) {

                data.id = this.filaId
            }
            else {

                data.opcaoEntradaId = this.form.opcaoEntradaId
            }

            return data
        },
        getPacienteData() {

            if (this.pacienteId) {

                return { id: this.pacienteId }
            }

            return {
                sexo_id: this.form.pacienteDesconhecido.sexoId,
                caracteristicas_fisicas: this.form.pacienteDesconhecido.caracteristicasFisicas,
            }
        },
        getUnidadeEncaminhamento(origemUnidade) {

            if (this.form.origemUnidadeValue === origemUnidade) {

                return this.form.unidadeEncaminhamento ? this.form.unidadeEncaminhamento.id : null
            }

            return null
        },
        reloadPaciente(paciente) {

            this.form.pacienteIdentificado.prontuario = paciente.prontuario
            this.form.pacienteIdentificado.cns = paciente.cidadao.cns ? this.formatCns(paciente.cidadao.cns) : null
            this.form.pacienteIdentificado.nome = paciente.cidadao.nome
            this.form.pacienteIdentificado.nomeSocial = paciente.cidadao.nomeSocial
            this.form.pacienteIdentificado.nomeMae = paciente.cidadao.nomeMae
            this.form.pacienteIdentificado.sexo.id = paciente.cidadao.sexo.id
            this.form.pacienteIdentificado.sexo.descricao = paciente.cidadao.sexo.descricao
            this.form.pacienteIdentificado.dataNascimento = this.formatDate(paciente.cidadao.dataNascimento)
            this.form.pacienteIdentificado.idade.anos = paciente.cidadao.idade.anos
            this.form.pacienteIdentificado.idade.meses = paciente.cidadao.idade.meses
            this.form.pacienteIdentificado.idade.dias = paciente.cidadao.idade.dias

            if (paciente.cidadao.idade.anos >= 60) {

                this.form.isAtendimentoPreferencial = true
                this.form.prioridadeId = this.enums.prioridade.IDOSO
            }

            let endereco = ''

            if (paciente.cidadao.domicilio) {

                endereco = paciente.cidadao.domicilio.endereco
                endereco = endereco.completo + ' - ' + this.formatCep(endereco.logradouro.cep)
            }

            this.form.pacienteIdentificado.endereco = endereco

            this.form.pacienteIdentificado.telefones = paciente.cidadao.telefones.map(telefone => {

            telefone.numero = this.formatPhoneOrCellPhone(telefone.numero)

                return telefone
            })
        },
        handleEdit(item) {

            if (this.form.isPermissaoCadastro) {
                return true;
            }

            const classificacoes = this.form.classificacoes;
            const classificacaoId = item.classificacaoId;

            const isClassificacaoPresent = (classificacaoId) => {
                return classificacoes.some(classificacao => classificacao.id === classificacaoId);
            };

            switch (classificacaoId) {
                case this.enums.classificacao.MUNICIPE:
                case this.enums.classificacao.PROVISORIO:
                case this.enums.classificacao.OUTRO_MUNICIPIO:
                    return isClassificacaoPresent(classificacaoId);
                default:
                    return false;
            }
        }
    },
}
</script>

<style scoped>
.senha {
    border: 1px solid #ced4da;
}

.age {
    height: 34.5px;
    min-width: 140px;
    max-width: 225px;
    border: 1px solid #ced4da;
    border-radius: 0.50rem;
}

.age span {
    height: 1.14rem;
}
</style>